import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSession } from '@/lib/auth-client';
import { feedback, ApiError, type FeedbackCategory } from '@/lib/api';
import { MAP_COUNTRIES, mapPathFor } from '@/features/map-core/countries';

// Pages where the feedback button is suppressed:
//   - /quiz*       : user is mid-flow on the funnel; interrupting them
//                    with "rate us" feels off and pulls attention away from
//                    the question they're answering.
//   - /login       : pre-product surface; nothing to give feedback on.
//   - /<cc>/map    : map shell has its own crowded UI (filter panel
//                    bottom-left, zoom controls bottom-right). The dark
//                    floating Feedback chip stacked over the filter panel
//                    was a direct visual collision — feedback access on
//                    the map is moved into the LayerSwitcher kebab menu
//                    instead (see LayerSwitcher.tsx). Every country map
//                    shares that shell, so the list comes from the map
//                    registry rather than a hardcoded '/it/map'.
const FEEDBACK_HIDDEN_PREFIXES = [
  '/quiz',
  '/login',
  ...MAP_COUNTRIES.flatMap((cc) => mapPathFor(cc) ?? []),
];

/**
 * F6.5 — Floating "Send feedback" button + modal.
 *
 * Open to everyone (anon + logged-in). Categories: bug / idea / data /
 * other so we can triage incoming reports. Server attaches userId when
 * cookie is present; for anon submitters we expose an optional email
 * field so we can reply.
 *
 * Rate-limited backend-side (5 submissions / 15 min / IP) so a stuck
 * key combo or bot can't fill D1.
 */

const CATEGORIES: { id: FeedbackCategory; emoji: string; defaultLabel: string }[] = [
  { id: 'bug', emoji: '🐞', defaultLabel: 'Bug' },
  { id: 'idea', emoji: '💡', defaultLabel: 'Idea' },
  { id: 'data', emoji: '📊', defaultLabel: 'Data error' },
  { id: 'other', emoji: '💬', defaultLabel: 'Other' },
];

export function FeedbackButton() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('idea');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suppress on funnel-flow / pre-product surfaces (see prefix list).
  // All hooks must be called before this early return to satisfy Rules of Hooks.
  const isHidden = FEEDBACK_HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  const mutation = useMutation({
    mutationFn: () =>
      feedback.submit({
        category,
        message: message.trim(),
        email: !session && email.trim() ? email.trim() : undefined,
        pageUrl: window.location.href,
      }),
    onSuccess: () => {
      setDone(true);
      setMessage('');
      setEmail('');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 429) {
        setError(
          t('feedback.errorRateLimit', {
            defaultValue: 'Too many submissions. Try again later.',
          }),
        );
      } else {
        setError(
          e instanceof Error
            ? e.message
            : t('feedback.errorGeneric', { defaultValue: 'Could not submit. Try again.' }),
        );
      }
    },
  });

  const closeDialog = () => {
    setOpen(false);
    setDone(false);
    setError(null);
  };

  if (isHidden) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Positioning: bottom-LEFT to avoid the AI helper bubble which lives
        // at bottom-right. Earlier we stacked them both at right with bottom-24
        // for feedback and bottom-4 for the bubble — same x-axis + same 💬
        // emoji made them read as "two avatars". Splitting corners + swapping
        // the icon to an envelope removes the visual conflict.
        className="fixed bottom-4 left-4 z-[1100] hidden h-10 items-center gap-1.5 rounded-full bg-[color:var(--color-ink-800)] px-3.5 text-xs font-medium text-[color:var(--color-paper)] shadow-lg hover:bg-[color:var(--color-ink-900)] sm:inline-flex"
        title={t('feedback.openTitle', { defaultValue: 'Send feedback' })}
        aria-label={t('feedback.openTitle', { defaultValue: 'Send feedback' })}
        data-testid="feedback-open"
      >
        <span aria-hidden="true">✉</span>
        {t('feedback.openLabel', { defaultValue: 'Feedback' })}
      </button>

      {open && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
          {/* Backdrop — separate from dialog so the dialog role itself has no click handler */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeDialog}
            aria-label="Close dialog"
            tabIndex={-1}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            className="relative w-full max-w-md rounded-lg bg-[color:var(--color-paper)] shadow-2xl"
          >
            <header className="border-b border-slate-200 px-5 py-3">
              <h2
                id="feedback-title"
                className="font-serif text-lg font-medium text-[color:var(--color-ink-900)]"
              >
                ✉ {t('feedback.title', { defaultValue: 'Send feedback' })}
              </h2>
              <p className="mt-0.5 text-xs text-[color:var(--color-ink-500)]">
                {t('feedback.subtitle', {
                  defaultValue: 'Found a bug, missing data, or have an idea? Tell us.',
                })}
              </p>
            </header>

            {done ? (
              <div className="px-5 py-6 text-center">
                <div className="mb-2 text-3xl">✅</div>
                <p className="text-sm text-[color:var(--color-ink-700)]">
                  {t('feedback.thanks', { defaultValue: 'Thanks! We read every report.' })}
                </p>
                <button
                  type="button"
                  onClick={closeDialog}
                  className="mt-4 rounded-md bg-[color:var(--color-terra-600)] px-4 py-1.5 text-sm font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)]"
                >
                  {t('common.close', { defaultValue: 'Close' })}
                </button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setError(null);
                  if (message.trim().length < 5) {
                    setError(t('feedback.tooShort', { defaultValue: 'Add at least a sentence.' }));
                    return;
                  }
                  mutation.mutate();
                }}
                className="px-5 py-4"
              >
                <fieldset className="mb-3">
                  <legend className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
                    {t('feedback.category', { defaultValue: 'Category' })}
                  </legend>
                  <div className="flex flex-wrap gap-1">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategory(cat.id)}
                        className={`rounded-full px-3 py-1 text-xs transition-colors ${
                          category === cat.id
                            ? 'bg-[color:var(--color-terra-600)] text-[color:var(--color-paper)]'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        aria-pressed={category === cat.id}
                      >
                        {cat.emoji}{' '}
                        {t(`feedback.cat.${cat.id}`, { defaultValue: cat.defaultLabel })}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="mb-3 block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
                    {t('feedback.message', { defaultValue: 'Message' })} *
                  </span>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- focus trap for accessibility: dialog textarea should receive focus on open
                    autoFocus
                    placeholder={t('feedback.placeholder', {
                      defaultValue: 'What happened, where, and what did you expect?',
                    })}
                    className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-[color:var(--color-terra-600)] focus:outline-none"
                  />
                </label>

                {!session && (
                  <label className="mb-3 block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
                      {t('feedback.email', { defaultValue: 'Email (optional, for reply)' })}
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={254}
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-[color:var(--color-terra-600)] focus:outline-none"
                    />
                  </label>
                )}

                {error && (
                  <div role="alert" className="mb-3 rounded bg-red-50 px-3 py-1.5 text-xs text-red-700">
                    {error}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={mutation.isPending}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {t('common.cancel', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type="submit"
                    disabled={mutation.isPending || message.trim().length < 5}
                    className="rounded-md bg-[color:var(--color-terra-600)] px-4 py-1.5 text-sm font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] disabled:opacity-50"
                  >
                    {mutation.isPending
                      ? t('feedback.sending', { defaultValue: 'Sending…' })
                      : t('feedback.send', { defaultValue: 'Send' })}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
